import Alert from '@risk-smart/themed-cloudscape-components/alert';
import Spinner from '@risk-smart/themed-cloudscape-components/spinner';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import {
  EMPTY_PREFERENCES_SET,
  ENABLED_CHANNELS,
  type PreferencesSet,
  preferencesSetSchema,
} from '@risksmart-app/shared/knock/schemas';
import type { TestSendWorkflowKey } from '@risksmart-app/trpc/src/routers/frontend/notifications/test-send-keys';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalForm } from 'src/components/form/form/ModalForm';

import NotificationSettingsForm from '@/components/notification-settings-modal/NotificationSettingsForm';
import { applyChildPromotions } from '@/components/notification-settings-modal/promotion';
import { useWorkflows } from '@/components/notification-settings-modal/util';
import { useDisconnectSlack } from '@/hooks/mutations/notifications/useDisconnectSlack';
import { useUpdateNotificationPreferences } from '@/hooks/mutations/notifications/useUpdateNotificationPreferences';
import { useSendTestNotifications } from '@/hooks/notifications/useSendTestNotifications';
import { useGetNotificationPreferences } from '@/hooks/queries/notifications/useGetNotificationPreferences';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useSlack } from '@/hooks/useSlack';
import { handleError } from '@/utils/errorUtils';

type Props = {
  onClose: () => void;
};

const NotificationSettingsModal = ({ onClose }: Props) => {
  const { loginWithSlack } = useSlack();
  const { disconnectSlack, loading: slackLoading } = useDisconnectSlack();
  const { data, loading, error, refetch } = useGetNotificationPreferences();

  const { t } = useTranslation(['common'], {
    keyPrefix: 'notification_settings',
  });
  const { t: tt } = useTranslation(['common']);
  const { updateNotificationPreferences } = useUpdateNotificationPreferences();
  const onSave = useCallback(
    async (preferenceSet: PreferencesSet) => {
      await updateNotificationPreferences({ preferenceSet });
    },
    [updateNotificationPreferences]
  );
  const workflows = useWorkflows();

  // QA tooling: send fixture notifications to yourself (see useSendTestNotifications).
  // The flag hides the buttons here; the server enforces it independently.
  const testSendEnabled = useIsFeatureFlagEnabled('notification_test_send');
  const sendTestNotifications = useSendTestNotifications();
  const { addNotification } = useNotifications();
  const onSendTest = useCallback(
    async (workflowKey?: TestSendWorkflowKey) => {
      try {
        const { results } = await sendTestNotifications.mutateAsync({
          workflowKey,
        });
        const failed = results.filter((r) => r.status === 'failed');
        if (failed.length > 0) {
          addNotification({
            type: 'error',
            content: t('test_send.partial_failure', {
              failed: failed.length,
              total: results.length,
            }),
          });
        } else {
          addNotification({
            type: 'success',
            content: t('test_send.success', { count: results.length }),
          });
        }
      } catch (error) {
        // Toast for the user AND route to Sentry/console — a broken test-send
        // integration must not fail silently in observability terms.
        handleError(error);
        addNotification({ type: 'error', content: t('test_send.error') });
      }
    },
    [sendTestNotifications, addNotification, t]
  );

  const isSlackConnected = useMemo(() => {
    return !!data?.slackNotificationConnection.connected;
  }, [data]);

  // Pre-process values to promote top-level channels based on child settings and normalize workflow data
  const promotedValues = useMemo(() => {
    if (!data?.notificationPreferences) {
      return EMPTY_PREFERENCES_SET;
    }

    return applyChildPromotions(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data.notificationPreferences as any,
      ENABLED_CHANNELS
    );
  }, [data?.notificationPreferences]);

  // Create a stable key for the form to prevent React Hook Form re-initialization during data loading
  // This ensures consistent form behavior between first and subsequent loads
  const formKey = useMemo(() => {
    if (!data?.notificationPreferences) {
      return 'empty';
    }

    return `form-${data.notificationPreferences.id}`;
  }, [data?.notificationPreferences]);

  return (
    <ModalForm
      key={formKey}
      formId={'notification-settings'}
      values={promotedValues}
      defaultValues={EMPTY_PREFERENCES_SET}
      onSave={onSave}
      schema={preferencesSetSchema}
      i18n={tt('notification_settings')}
      visible={true}
      onDismiss={onClose}
      secondaryActions={
        loading || !data?.notificationPreferences
          ? []
          : [
              ...(testSendEnabled
                ? [
                    {
                      label: t('test_send.send_all'),
                      action: () => onSendTest(),
                      loading: sendTestNotifications.isPending,
                    },
                  ]
                : []),
              {
                label: isSlackConnected
                  ? t('slackButton.disconnect')
                  : t('slackButton.connect'),
                action: async () => {
                  if (isSlackConnected) {
                    disconnectSlack().then(() => refetch());
                  } else {
                    // Awaited rather than `.then(…).catch()`: a bare `.catch()`
                    // takes no handler, so it re-propagated instead of catching
                    // and the rejection surfaced as an unhandled one. Reachable
                    // since COI-709 gave `loginWithSlack` a network call
                    // (minting the OAuth state).
                    try {
                      await loginWithSlack();
                    } catch {
                      // Already reported and toasted inside the hook.
                      return;
                    }

                    // Reported separately: the hook only reports LOGIN failures,
                    // so a refetch rejection folded into the catch above would be
                    // discarded — leaving the connection state stale on screen
                    // with nothing logged.
                    try {
                      await refetch();
                    } catch (error) {
                      handleError(error);
                    }
                  }
                },
                loading: slackLoading,
              },
            ]
      }
    >
      {loading ? (
        <div className={'grid place-items-center p-4'}>
          <Spinner size={'big'} />
        </div>
      ) : error ? (
        <Alert
          statusIconAriaLabel={'Error'}
          type={'error'}
          header={`Failed to load ${t('entity_name')}`}
        />
      ) : (
        <NotificationSettingsForm
          workflows={workflows}
          enabledChannels={ENABLED_CHANNELS}
          defaultDisabledChannels={isSlackConnected ? [] : ['chat']}
          onSendTest={testSendEnabled ? onSendTest : undefined}
        />
      )}
    </ModalForm>
  );
};

export default NotificationSettingsModal;
